import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { devLogin, getCurrentSession } from '@/api/auth'
import type { AppRole, AuthLoginResponse, AuthSession } from '@/api/types/auth'
import { ApiError } from '@/api/client'

const AUTH_TOKEN_KEY = 'auth_token'
const AUTH_SESSION_KEY = 'auth_session'

interface AuthContextValue {
  session: AuthSession | null
  isAuthenticated: boolean
  isBootstrapping: boolean
  login: (payload: { userId: string; userName: string; role: AppRole }) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readStoredSession(): AuthSession | null {
  const raw = localStorage.getItem(AUTH_SESSION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthSession
  } catch {
    localStorage.removeItem(AUTH_SESSION_KEY)
    return null
  }
}

function writeAuthSession(result: AuthLoginResponse | AuthSession) {
  localStorage.setItem(
    AUTH_SESSION_KEY,
    JSON.stringify({
      userId: result.userId,
      userName: result.userName,
      role: result.role,
      issuedAtEpochSecond: 'issuedAtEpochSecond' in result ? result.issuedAtEpochSecond : undefined,
      expiresAtEpochSecond: 'expiresAtEpochSecond' in result ? result.expiresAtEpochSecond : undefined,
    } satisfies AuthSession)
  )
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [session, setSession] = useState<AuthSession | null>(() => readStoredSession())
  const [isBootstrapping, setIsBootstrapping] = useState(true)

  const clearSession = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_SESSION_KEY)
    setSession(null)
  }, [])

  const logout = useCallback(() => {
    clearSession()
    navigate('/login', { replace: true })
  }, [clearSession, navigate])

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY)
    if (!token) {
      setIsBootstrapping(false)
      return
    }

    const stored = readStoredSession()
    if (stored) {
      setSession(stored)
      setIsBootstrapping(false)
      return
    }

    getCurrentSession()
      .then((nextSession) => {
        writeAuthSession(nextSession)
        setSession(nextSession)
      })
      .catch(() => {
        clearSession()
      })
      .finally(() => setIsBootstrapping(false))
  }, [clearSession])

  useEffect(() => {
    const handleUnauthorized = () => {
      clearSession()
      navigate('/login', { replace: true })
    }
    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [clearSession, navigate])

  const login = useCallback(
    async (payload: { userId: string; userName: string; role: AppRole }) => {
      const response = await devLogin(payload)
      localStorage.setItem(AUTH_TOKEN_KEY, response.token)
      writeAuthSession(response)
      setSession({
        userId: response.userId,
        userName: response.userName,
        role: response.role,
      })
      navigate('/dashboard', { replace: true })
    },
    [navigate]
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: !!session,
      isBootstrapping,
      login,
      logout,
    }),
    [isBootstrapping, login, logout, session]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function isUnauthorizedError(error: unknown) {
  return error instanceof ApiError && error.status === 401
}
